import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAgent, RagAgent } from "src/features/rag/api/ragApi";
import RagChat from "src/features/rag/components/RagChat";

// Wixarpedia is permanently bound to the shared "Sophie" RAG agent (id 3 —
// World famous Sophie from Wixar). There is intentionally no agent picker:
// the agent cannot be changed or unselected from this surface.
const SOPHIE_AGENT_ID = 3;

const WixarpediaChat: React.FC = () => {
  const { t } = useTranslation();
  const [agent, setAgent] = useState<RagAgent | null>(null);
  const [loading, setLoading] = useState(true);

  const loadAgent = useCallback(async () => {
    try {
      const data = await fetchAgent(SOPHIE_AGENT_ID);
      setAgent(data);
    } catch (err) {
      console.error("Failed to load Wixarpedia agent:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgent();
  }, [loadAgent]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-3">
          <div className="w-12 h-12 bg-gray-200 rounded-xl" />
          <div className="h-4 bg-gray-200 rounded w-32" />
        </div>
      </div>
    );
  }

  // Sophie is a curated shared agent; document_count drives the same
  // empty/disabled state RagChat uses on the Agents page.
  const hasDocuments = (agent?.document_count ?? 0) > 0;

  // Mirror the ScenarIA shell (ScenarioHelperChat) so the chat column lands
  // at the exact same width. The side columns are empty, borderless spacers
  // (Wixarpedia has no saved-conversation history and no agent picker).
  return (
    <div
      className="grid h-full w-full overflow-hidden"
      style={{ gridTemplateColumns: "240px 1fr 280px" }}
    >
      <div aria-hidden="true" />

      <div className="min-w-0 overflow-hidden p-2">
        <div className="h-full bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col min-h-0">
          <RagChat
            agentId={SOPHIE_AGENT_ID}
            hasDocuments={hasDocuments}
            showHeader={false}
            emptyText={t("pages.agents.chat.wixarpediaEmptyChat")}
          />
        </div>
      </div>

      <div aria-hidden="true" />
    </div>
  );
};

export default WixarpediaChat;
